/* global eventStartDate eventEndDate newProcurement fetchProcurementPlanItemsUrl fetchUrl deleteProcurementPlanItemAttachmentUrl listUsersUrl fetchDepartmentsUrl */
/* global expensesUrl fetchProcurementPlanItemUrl deleteProcurementPlanItemUrl createPOUrl downloadProcurementPlanItemAttachmentUrl syncProcurementPlanProjectsUrl */
/* global partialUpdateProcurementPlanItemUrl updateProcurementPlanItemUrl syncProcurementPlanItemExpensesUrl syncProcurementPlanItemExpenseablesUrl */
/* global prEnabled eaEnabled directCommitEnabled storePRUrl indexPRUrl directCommitmentUrl directPurchaseOrderUrl refreshCommitmentDataUrl storeProcurementPlanItemUrl */
/* global draftsIndexUrl deleteDraftUrl */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { Modal } from 'flowbite';

import ProcurementEditModal from './ProcurementEditModal';
import ProcurementModal from './ProcurementModal';
import ProcurementPlanItemsTable from './ProcurementPlanItemsTable';
import ProcurementPrModal from './ProcurementPrModal';

import { modalOptions } from 'Helpers/modalOptions';
import { apiClient, isSetupGateError } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import SelectField from 'CommonComponents/SelectField';
import TablePagination from 'CommonComponents/TablePagination';
import TableSearchInput from 'CommonComponents/TableSearchInput';
import ToggleInput from 'CommonComponents/ToggleInput';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import { UsersProvider } from 'Context/UsersContext';

import { useCustomEvent } from 'Hooks/useGlobalDOMEvents';
import useModal from 'Hooks/useModal';
import usePagination from 'Hooks/usePagination';
import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';
import { useUsers } from 'Hooks/useUsers';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import SortIcon from 'Assets/icons/sort.svg';

const INITIAL_LOADING_STATE = { procurements: true };
const INITIAL_EXPENSE_PARAMS_STATE = { sort_by: 'name', order: 'asc' };

const queryParams = new URLSearchParams(window.location.search);
const query = { action: queryParams.get('action'), procurementId: queryParams.get('procurement_id') };

export default function ProcurementPlanIndex(props) {
    const [policies, setPolicies] = useState([]);
    const [categories, setCategories] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [complexities, setComplexities] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [plan, setPlan] = useState(null);
    const [currentProcurement, setCurrentProcurement] = useState(null);
    const [confirmationText, setConfirmationText] = useState(null);
    const [expensesTree, setExpensesTree] = useState([]);
    const [existedIds, setExistedIds] = useState([]);
    const [otherDeptLockedExpenseIds, setOtherDeptLockedExpenseIds] = useState([]);
    const [isLoading, setIsLoading] = useState(INITIAL_LOADING_STATE);
    const [isFetched, setIsFetched] = useState({});
    const [isCreatePrMode, setIsCreatePrMode] = useState(false);
    const [isSyncProjectsEnabled, setIsSyncProjectsEnabled] = useState(false);
    const [reSyncing, setReSyncing] = useState(false);
    const [filters, setFilters] = useState({ owner: undefined, department: undefined });

    const [expenseParams, setExpenseParams] = useState(INITIAL_EXPENSE_PARAMS_STATE);

    const procurementCreateModal = useModal({ visible: query.action === 'create_procurement' });
    const procurementEditModal = useModal();
    const procurementModal = useModal();
    const procurementPrModal = useModal();

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);
    const [deleteFailedMsg, setDeleteFailedMsg] = useState('');
    const [procurementItemDeleteModalElement, setProcurementItemDeleteModalElement] = useState(null);
    const [projectsSyncConfirmModalElement, setProjectsSyncConfirmModalElement] = useState(null);
    const procurementItemDeleteModalRef = useRef();
    const projectsSyncConfirmModalRef = useRef();
    const latestRequest = useRef(null);

    const toast = useToast();

    const { users } = useUsers();

    const outstandingProcurementsPagination = usePagination({ perPage: 25 });
    const completedProcurementsPagination = usePagination({ perPage: 25 });

    const departmentsRequest = useQuery({
        queryFn: () => apiClient.get(props.fetchDepartmentsUrl),
    });

    const outstandingProcurementsRequest = useQuery({
        enabled: false,
        queryFn: () =>
            apiClient.get(fetchProcurementPlanItemsUrl, {
                params: {
                    owner: filters.owner,
                    department: props.departmentContextId ? props.departmentContextId : filters.department,
                    search: filters.search,
                    status: 'outstanding',
                    per_page: outstandingProcurementsPagination.perPage,
                    page: outstandingProcurementsPagination.page,
                },
            }),
        successFn: (data) => {
            outstandingProcurementsPagination.setFrom(data.meta.from);
            outstandingProcurementsPagination.setTo(data.meta.to);
            outstandingProcurementsPagination.setTotal(data.meta.total);
            outstandingProcurementsPagination.setLastPage(data.meta.last_page);

            // If the current page is greater than 1 and there are no items on the current page,
            // it means the user has deleted the last item on that page.
            // In this case, we should go back to the previous page.
            if (outstandingProcurementsPagination.page > 1 && data.data.length === 0) {
                outstandingProcurementsPagination.setPage(outstandingProcurementsPagination.page - 1);
            }
        },
    });

    const completedProcurementsRequest = useQuery({
        enabled: false,
        queryFn: () =>
            apiClient.get(fetchProcurementPlanItemsUrl, {
                params: {
                    owner: filters.owner,
                    department: props.departmentContextId ? props.departmentContextId : filters.department,
                    search: filters.search,
                    status: 'completed',
                    per_page: completedProcurementsPagination.perPage,
                    page: completedProcurementsPagination.page,
                },
            }),
        successFn: (data) => {
            completedProcurementsPagination.setFrom(data.meta.from);
            completedProcurementsPagination.setTo(data.meta.to);
            completedProcurementsPagination.setTotal(data.meta.total);
            completedProcurementsPagination.setLastPage(data.meta.last_page);

            // If the current page is greater than 1 and there are no items on the current page,
            // it means the user has deleted the last item on that page.
            // In this case, we should go back to the previous page.
            if (completedProcurementsPagination.page > 1 && data.data.length === 0) {
                completedProcurementsPagination.setPage(completedProcurementsPagination.page - 1);
            }
        },
    });

    const createProcurementRequest = useQuery({
        enabled: false,
        // The set-up gate surfaces its own message, so the generic one is only
        // shown for every other failure.
        displayErrorToast: false,
        successMessage: $t('Procurement created successfully'),
        queryFn: (params) => apiClient.post(props.storeProcurementPlanItemUrl.replace('PROCUREMENT_PLAN_ID', params.planId)),
        successFn: () => {
            outstandingProcurementsRequest.refetch();
        },
        errorFn: (error) => {
            if (!isSetupGateError(error)) {
                toast.error($t('Error creating Procurement'));
            }
        },
    });

    const departmentsOptions = useMemo(() => {
        return (
            departmentsRequest.data?.departments?.map((department) => ({
                label: department.name,
                value: department.id,
            })) || []
        );
    }, [departmentsRequest.data]);

    const emitRefetchProcurementDashboardEvent = useCustomEvent({
        eventName: 'REFETCH_PROCUREMENT_DASHBOARD',
    });

    useEffect(() => {
        fetchData();
        setProcurementItemDeleteModalElement(new Modal(procurementItemDeleteModalRef.current, modalOptions(false)));
        setProjectsSyncConfirmModalElement(new Modal(projectsSyncConfirmModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (plan) {
            fetchExpenses();
        }
    }, [plan]);

    useEffect(() => {
        outstandingProcurementsRequest.refetch();
    }, [filters, outstandingProcurementsPagination.page, outstandingProcurementsPagination.perPage]);

    useEffect(() => {
        completedProcurementsRequest.refetch();
    }, [filters, completedProcurementsPagination.page, completedProcurementsPagination.perPage]);

    useEffect(() => {
        if (plan) {
            fetchExpenses();
        }
    }, [expenseParams]);

    useEffect(() => {
        if (procurementEditModal.element) {
            const procurementId = queryParams.get('procurement_id');
            if (procurementId) {
                showEditProcurementModal({ id: procurementId });
            } else if (typeof newProcurement !== 'undefined' && newProcurement) {
                showEditProcurementModal(newProcurement);
            }
        }
    }, [procurementEditModal.element]);

    useEffect(() => {
        if (plan && plan.sync_projects_enabled !== isSyncProjectsEnabled) {
            syncProjects();
        }
    }, [isSyncProjectsEnabled]);

    const expensesIds = useMemo(() => {
        if (!expensesTree || !expensesTree.length) {
            return {};
        }

        const selectedData = {};

        const processParentStructures = (ps) => {
            ps.forEach((item) => {
                item.expenses.forEach((expense) => {
                    selectedData[expense.id] = false;
                });

                if (item.type === 'template') {
                    const dividers = _.isArray(item.dividers) ? item.dividers : Object.values(item.dividers);
                    processParentStructures(dividers);
                }
            });
        };

        const processExp = (parent) => {
            parent.expenses.forEach((expense) => {
                selectedData[expense.id] = false;
            });

            const packs = _.isArray(parent.packs) ? parent.packs : Object.values(parent.packs);
            processParentStructures(packs);

            const templates = _.isArray(parent.templates) ? parent.templates : Object.values(parent.templates);
            processParentStructures(templates);
        };

        expensesTree.forEach((department) => {
            Object.values(department.costcenters).forEach((cc) => {
                processExp(cc);
                const projects = _.isArray(cc.projects) ? cc.projects : Object.values(cc.projects);
                projects.forEach((project) => {
                    processExp(project);
                });
            });
        });

        return selectedData;
    }, [expensesTree]);

    const editModalLockedExpenseIds = useMemo(() => {
        if (!props.departmentContextId) {
            return undefined;
        }

        const procurementLineLocked = currentProcurement?.dept_owned_expense_ids
            ? (currentProcurement.expenses || []).filter((e) => !currentProcurement.dept_owned_expense_ids.includes(e.id)).map((e) => e.id)
            : [];

        return [...new Set([...otherDeptLockedExpenseIds, ...procurementLineLocked])];
    }, [props.departmentContextId, otherDeptLockedExpenseIds, currentProcurement]);

    const loading = (key, value = true) => {
        setIsLoading((pervLoading) => ({ ...pervLoading, [key]: value }));
    };

    const fetched = (key, value = true) => {
        setIsFetched((pervLoading) => ({ ...pervLoading, [key]: value }));
    };

    const setOrdering = (e, sort) => {
        e.preventDefault();
        const sortingOrder = sort === expenseParams.sort_by ? (expenseParams.order === 'asc' ? 'desc' : 'asc') : 'asc';
        setExpenseParams((params) => ({ ...params, sort_by: sort, order: sortingOrder }));
    };

    const arrow = (name) => {
        if (name === expenseParams.sort_by) {
            return expenseParams.order === 'asc' ? <ChevronDownIcon className="tw-w-2 tw-h-2" /> : <ChevronUpIcon className="tw-w-2 tw-h-2" />;
        }

        return <SortIcon className="tw-w-3 tw-h-3" />;
    };

    const fetchData = () => {
        if (isLoading.data) {
            return;
        }

        loading('data');
        fetched('data', false);

        axios
            .untranslated()
            .get(fetchUrl)
            .then(({ data }) => {
                setPlan(data.plan);
                setPolicies(Array.isArray(data.policies) ? data.policies : []);
                setCategories(data.categories);
                setPriorities(data.priorities);
                setComplexities(data.complexities);
                setStatuses(data.statuses);
                setIsSyncProjectsEnabled(data.plan.sync_projects_enabled);

                fetched('data');

                const procurementId = query.procurementId;
                if (procurementId) {
                    showEditProcurementModal({ id: procurementId });
                }
            })
            .catch((e) => {
                console.log(e);
                fetched('data', false);
            })
            .finally(() => {
                loading('data', false);
            });
    };

    const fetchExpenses = () => {
        loading('expenses');
        fetched('expenses', false);

        axios
            .get(expensesUrl, { params: expenseParams })
            .then(({ data }) => {
                setExpensesTree(data.expensesTree);
                setExistedIds(data.excludedExpensesIds);
                setOtherDeptLockedExpenseIds(data.lockedExpenseIds ?? []);

                fetched('expenses');
            })
            .catch((e) => {
                console.log(e);
                fetched('expenses', false);
            })
            .finally(() => {
                loading('expenses', false);
            });
    };

    const fetchProcurement = (id, forTooltip = false, forReSyncing = false) => {
        if (id) {
            let url = fetchProcurementPlanItemUrl.replace('ITEM_ID', id);
            if (forTooltip) {
                latestRequest.current = id;

                // Create a URL object
                const urlObject = new URL(url);

                // Add the new parameter
                urlObject.searchParams.set('expanded', 'true');

                // Get the updated URL string
                url = urlObject.toString();
            }
            if (!forReSyncing) {
                loading('procurement');
                fetched('procurement', false);
            } else {
                setReSyncing(true);
            }

            axios
                .get(url)
                .then(({ data }) => {
                    if (forTooltip) {
                        if (latestRequest.current === id) {
                            setCurrentProcurement(data);
                            if (!forReSyncing) {
                                fetched('procurement');
                            }
                        }
                    } else {
                        setCurrentProcurement(data);
                        if (!forReSyncing) {
                            fetched('procurement');
                        }
                    }
                })
                .catch((e) => {
                    console.error(e);
                    if (!forReSyncing) {
                        fetched('procurement', false);
                    } else {
                        setReSyncing(false);
                    }
                })
                .finally(() => {
                    if (forTooltip) {
                        if (latestRequest.current === id) {
                            if (!forReSyncing) {
                                loading('procurement', false);
                            } else {
                                setReSyncing(false);
                            }
                        }
                    } else {
                        if (!forReSyncing) {
                            loading('procurement', false);
                        } else {
                            setReSyncing(false);
                        }
                    }
                });
        }
    };

    const triggerDeleteProcurementItem = (procurement) => {
        setActionInProgress(true);

        const url = deleteProcurementPlanItemUrl.replace('ITEM_ID', currentProcurement.id);

        axios
            .delete(url)
            .then(() => {
                fetchExpenses();
                completedProcurementsRequest.refetch();
                outstandingProcurementsRequest.refetch();
                setActionCompleted(true);

                setTimeout(
                    () => emitRefetchProcurementDashboardEvent(),
                    1000 * 5, //? wait for 5 seconds, supposing the dashboard calculations job will be done.
                );
            })
            .catch((e) => {
                console.error(e);
                const serverMsg = e?.response?.data?.message;
                setDeleteFailedMsg(serverMsg || $t('Could not be deleted'));
                setActionFailed(true);
            });
    };

    const syncProjects = () => {
        axios
            .put(props.syncProcurementPlanProjectsUrl, { enabled: isSyncProjectsEnabled })
            .then(() => {
                toast.success($t('Projects synced successfully'));
                fetchData();
            })
            .catch((e) => {
                setIsSyncProjectsEnabled(false);
                toast.error($t('Error syncing Projects with Procurement Plan'));
            });
    };

    const deleteProcurement = (procurement, isCompleted = false) => {
        if (props.departmentContextId && procurement.is_multi_dept) {
            // Multi-department procurement — delete is disabled in dept context; do nothing
            return;
        }

        let confirmationText = !isCompleted
            ? $t('Are you sure you want delete this procurement?')
            : $t('This is a completed procurement - are you sure you want to delete it?');

        setCurrentProcurement(procurement);
        setConfirmationText(confirmationText);
        procurementItemDeleteModalElement.show();
    };

    const createPR = (procurement) => {
        setIsCreatePrMode(true);
        showProcurementPrModal(procurement);
    };

    const createPO = (procurement) => {
        window.location.href = procurement.purchase_requisition_id
            ? createPOUrl.replace('REQUISITION_ID', procurement.purchase_requisition_id)
            : props.directPurchaseOrderUrl.replace('PROCUREMENT_PLAN_ID', procurement.plan_id).replace('ITEM_ID', procurement.id);
    };

    const onDirectCommit = (procurement) => {
        window.location.href = props.directCommitmentUrl.replace('PROCUREMENT_PLAN_ID', procurement.plan_id).replace('ITEM_ID', procurement.id);
    };

    const onValueHover = (procurement) => {
        if (procurement?.id) {
            fetchProcurement(procurement.id, true);
        }
    };

    const showEditProcurementModal = (procurement) => {
        fetchProcurement(procurement?.id);
        procurementEditModal.show();
    };

    const showProcurementPrModal = (procurement) => {
        fetchProcurement(procurement?.id);
        procurementPrModal.show();
    };

    const showProcurementModal = (procurement) => {
        fetchProcurement(procurement?.id);
        procurementModal.show();
    };

    const closeModal = () => {
        setIsCreatePrMode(false);
        fetched('procurement', false);
        setCurrentProcurement(null);
        procurementEditModal.hide();
        procurementCreateModal.hide();
        procurementPrModal.hide();
        procurementModal.hide();
    };

    const cleanProcurementItemDeleteModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
        setDeleteFailedMsg('');
    }, [actionInProgress, actionFailed, actionCompleted]);

    const hideModalElement = (modalElement) => {
        modalElement && modalElement.hide();
    };

    const successHandlerDelete = () => {
        hideModalElement(procurementItemDeleteModalElement);
    };

    const onSyncProjectsToggle = (enabled) => {
        if (!enabled) {
            return projectsSyncConfirmModalElement.show();
        }

        setIsSyncProjectsEnabled(enabled);
    };

    return (
        <div>
            <div className="table__card">
                <div className="tw-flex tw-items-start tw-justify-end tw-mb-6">
                    <div className="tw-flex tw-items-center tw-gap-x-2">
                        <div>
                            <TableSearchInput
                                searchValue={filters.search}
                                handleEnterPress={(e, value) => setFilters((prev) => ({ ...prev, search: value }))}
                            />
                        </div>
                        <div className="tw-w-40">
                            <UserSelectorInput
                                clearable
                                selectedId={filters.owner}
                                usersList={users}
                                fetchIfNoUsers={false}
                                placeholder={$t('Owner')}
                                onChange={(value) => {
                                    if (value === filters.owner) {
                                        return;
                                    }
                                    setFilters((prev) => ({ ...prev, owner: value || undefined }));
                                }}
                            />
                        </div>
                        {!props.departmentContextId && (
                            <div className="tw-w-40">
                                <SelectField
                                    clearable
                                    options={departmentsOptions}
                                    value={filters.department}
                                    placeholder={$t('Department')}
                                    onChange={(option) => setFilters((prev) => ({ ...prev, department: option?.value || undefined }))}
                                />
                            </div>
                        )}
                        {plan && !props.departmentContextId && (
                            <div className="-tw-mb-2">
                                <ToggleInput
                                    label="Insert Procurement Plan into Project Plan"
                                    checked={!!isSyncProjectsEnabled}
                                    onChange={(e) => onSyncProjectsToggle(e.target.checked)}
                                />
                            </div>
                        )}
                    </div>
                </div>
                {/* IB-610 finding #2: Saved Drafts now live on the EAs and DCs tab (a held draft is
                    an EA/DC), not the Plan tab — keeping a single source of truth. The cost-line
                    locking behaviour on this tab is unchanged. */}
                <div className="tw-flex tw-items-start tw-justify-between tw-mb-6">
                    <div className="tw-flex tw-flex-col tw-gap-y-1">
                        <h4 className="table__header">{$t('Outstanding Procurements')}</h4>
                        <div className="table__pagination-info">
                            <PaginationInfo
                                to={outstandingProcurementsPagination.to}
                                from={outstandingProcurementsPagination.from}
                                total={outstandingProcurementsPagination.total}
                            />
                            <TablePagination
                                lastPage={outstandingProcurementsPagination.lastPage}
                                pageChange={(data) => outstandingProcurementsPagination.setPage(+data.selected + 1)}
                                currentPage={outstandingProcurementsPagination.page}
                            />
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-x-2">
                        <div>
                            <PerPageSelect
                                value={outstandingProcurementsPagination.perPage}
                                onChange={(e) => {
                                    outstandingProcurementsPagination.setPerPage(parseInt(e.target.value));
                                }}
                            />
                        </div>

                        <Button
                            variant="primary"
                            onClick={() => createProcurementRequest.refetch({ planId: plan.id })}
                            loading={createProcurementRequest.loading}
                            disabled={!plan}
                        >
                            {$t('Add Procurement')}
                        </Button>
                    </div>
                </div>
                <ProcurementPlanItemsTable
                    planItems={outstandingProcurementsRequest.data}
                    procurement={currentProcurement}
                    loadingProcurement={isLoading.procurement}
                    isLoadingProcurements={outstandingProcurementsRequest.loading}
                    policies={policies}
                    priorities={priorities}
                    complexities={complexities}
                    pagination={outstandingProcurementsPagination}
                    prEnabled={props.prEnabled}
                    eaEnabled={props.eaEnabled}
                    directCommitEnabled={props.directCommitEnabled}
                    partialUpdateUrl={partialUpdateProcurementPlanItemUrl}
                    departmentContextId={props.departmentContextId}
                    onEdit={(procurement) => showEditProcurementModal(procurement)}
                    onView={(procurement) => showProcurementModal(procurement)}
                    onDelete={(procurement) => deleteProcurement(procurement)}
                    onCreatePR={(procurement) => createPR(procurement)}
                    onCreatePO={(procurement) => createPO(procurement)}
                    onDirectCommit={(procurement) => onDirectCommit(procurement)}
                    onValueHover={(procurement) => onValueHover(procurement)}
                    onUpdated={() => {
                        outstandingProcurementsRequest.refetch();
                    }}
                />

                <div className="tw-flex tw-items-start tw-justify-between tw-mb-6 tw-mt-12">
                    <div className="tw-flex tw-flex-col tw-gap-y-1">
                        <h4 className="table__header">{$t('Completed Procurements')}</h4>
                        <div className="table__pagination-info">
                            <PaginationInfo
                                to={completedProcurementsPagination.to}
                                from={completedProcurementsPagination.from}
                                total={completedProcurementsPagination.total}
                            />
                            <TablePagination
                                lastPage={completedProcurementsPagination.lastPage}
                                pageChange={(data) => completedProcurementsPagination.setPage(+data.selected + 1)}
                                currentPage={completedProcurementsPagination.page}
                            />
                        </div>
                    </div>
                    <div>
                        <PerPageSelect
                            value={completedProcurementsPagination.perPage}
                            onChange={(e) => {
                                completedProcurementsPagination.setPerPage(parseInt(e.target.value));
                            }}
                        />
                    </div>
                </div>
                <ProcurementPlanItemsTable
                    completed
                    policies={policies}
                    planItems={completedProcurementsRequest.data}
                    procurement={currentProcurement}
                    pagination={completedProcurementsPagination}
                    loadingProcurement={isLoading.procurement}
                    isLoadingProcurements={completedProcurementsRequest.loading}
                    prEnabled={props.prEnabled}
                    eaEnabled={props.eaEnabled}
                    departmentContextId={props.departmentContextId}
                    onEdit={(procurement) => showEditProcurementModal(procurement)}
                    onView={(procurement) => showProcurementModal(procurement)}
                    onValueHover={(procurement) => onValueHover(procurement)}
                    onDelete={(procurement) => deleteProcurement(procurement, true)}
                />
            </div>

            <ProcurementEditModal
                id="edit-procurement-modal"
                modal={procurementEditModal}
                procurement={currentProcurement}
                policies={policies}
                categories={categories}
                priorities={priorities}
                complexities={complexities}
                statuses={statuses}
                expensesTree={expensesTree}
                expensesIds={expensesIds}
                setOrdering={setOrdering}
                arrow={arrow}
                saveUrl={updateProcurementPlanItemUrl}
                storePRUrl={props.storePRUrl}
                indexPRUrl={props.indexPRUrl}
                syncExpensesUrl={syncProcurementPlanItemExpensesUrl}
                syncExpenseablesUrl={syncProcurementPlanItemExpenseablesUrl}
                downloadAttachmentUrl={downloadProcurementPlanItemAttachmentUrl}
                deleteAttachmentUrl={deleteProcurementPlanItemAttachmentUrl}
                existedIds={existedIds}
                lockedExpenseIds={editModalLockedExpenseIds}
                fetched={isFetched.data && isFetched.expenses && isFetched.procurement}
                loading={isLoading.data || isLoading.expenses || isLoading.procurement}
                isCreatePrMode={isCreatePrMode}
                reSyncing={reSyncing}
                setLoading={loading}
                eventStartDate={props.eventStartDate}
                eventEndDate={props.eventEndDate}
                onEdit={() => setIsCreatePrMode(false)}
                onCancelEdit={() => setIsCreatePrMode(true)}
                onClose={closeModal}
                onCancel={closeModal}
                onSuccess={() => {
                    outstandingProcurementsRequest.refetch();
                    completedProcurementsRequest.refetch();
                    fetchExpenses();
                }}
                onAttachmentDeleted={(id) => fetchProcurement(id)}
                onRefresh={(id) => fetchProcurement(id, false, true)}
                onExpensesSynced={(id) => {
                    fetchProcurement(id);
                    fetchExpenses();
                    outstandingProcurementsRequest.refetch();
                    completedProcurementsRequest.refetch();
                }}
            />
            <ProcurementPrModal
                id="procurement-pr-modal"
                modal={procurementPrModal}
                procurement={currentProcurement}
                policies={policies}
                categories={categories}
                priorities={priorities}
                complexities={complexities}
                statuses={statuses}
                expensesTree={expensesTree}
                expensesIds={expensesIds}
                setOrdering={setOrdering}
                arrow={arrow}
                saveUrl={updateProcurementPlanItemUrl}
                storePRUrl={props.storePRUrl}
                indexPRUrl={props.indexPRUrl}
                syncExpensesUrl={syncProcurementPlanItemExpensesUrl}
                syncExpenseablesUrl={syncProcurementPlanItemExpenseablesUrl}
                downloadAttachmentUrl={downloadProcurementPlanItemAttachmentUrl}
                deleteAttachmentUrl={deleteProcurementPlanItemAttachmentUrl}
                existedIds={existedIds}
                fetched={isFetched.data && isFetched.expenses && isFetched.procurement}
                loading={isLoading.data || isLoading.expenses || isLoading.procurement}
                isCreatePrMode={isCreatePrMode}
                setLoading={loading}
                eventStartDate={props.eventStartDate}
                eventEndDate={props.eventEndDate}
                onEdit={() => setIsCreatePrMode(false)}
                onCancelEdit={() => setIsCreatePrMode(true)}
                onClose={closeModal}
                onCancel={closeModal}
                onSuccess={() => {
                    outstandingProcurementsRequest.refetch();
                    completedProcurementsRequest.refetch();
                    fetchExpenses();
                }}
                onAttachmentDeleted={(id) => fetchProcurement(id)}
                onExpensesSynced={(id) => {
                    fetchProcurement(id);
                    fetchExpenses();
                    outstandingProcurementsRequest.refetch();
                    completedProcurementsRequest.refetch();
                }}
            />
            <ProcurementModal
                id="procurement-modal"
                modal={procurementModal}
                procurement={currentProcurement}
                fetched={isFetched.procurement}
                loading={isLoading.procurement}
                onClose={closeModal}
                downloadAttachmentUrl={downloadProcurementPlanItemAttachmentUrl}
            />
            <ConfirmationModal
                closeHandler={() => {}}
                successHandler={successHandlerDelete}
                triggerAction={triggerDeleteProcurementItem}
                cleanStates={cleanProcurementItemDeleteModalStates}
                allowAction={true}
                modalId={`delete-evaluation-template`}
                modalRef={procurementItemDeleteModalRef}
                modalElement={procurementItemDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={confirmationText}
                actionCompletedMsg={$t('Deleted')}
                actionFailedMsg={deleteFailedMsg || $t('Could not be deleted')}
                title={$t('Delete Evaluation Template')}
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Delete')}
            />
            <ConfirmationModal
                closeHandler={() => {}}
                triggerAction={() => {
                    setIsSyncProjectsEnabled(false);
                    hideModalElement(projectsSyncConfirmModalElement);
                }}
                allowAction={true}
                modalId="confirm-sync-projects"
                modalRef={projectsSyncConfirmModalRef}
                modalElement={projectsSyncConfirmModalElement}
                confirmationMsg={$t(
                    'Disabling the procurement plan will remove the Procurements and any associated actions from the Project Plan, are you sure you want to proceed?',
                )}
                title={$t('Disable the procurement plan')}
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Disable')}
            />
        </div>
    );
}

if (document.getElementById('procurementPlan')) {
    var root = document.getElementById('procurementPlan');
    ReactDOM.render(
        <App>
            <UsersProvider fetchUrl={listUsersUrl}>
                <ProcurementPlanIndex
                    prEnabled={prEnabled}
                    eaEnabled={eaEnabled}
                    directCommitEnabled={directCommitEnabled}
                    fetchUrl={fetchUrl}
                    expensesUrl={expensesUrl}
                    storePRUrl={storePRUrl}
                    indexPRUrl={indexPRUrl}
                    directCommitmentUrl={directCommitmentUrl}
                    directPurchaseOrderUrl={directPurchaseOrderUrl}
                    refreshCommitmentDataUrl={refreshCommitmentDataUrl}
                    syncProcurementPlanProjectsUrl={syncProcurementPlanProjectsUrl}
                    fetchDepartmentsUrl={fetchDepartmentsUrl}
                    listUsersUrl={listUsersUrl}
                    storeProcurementPlanItemUrl={storeProcurementPlanItemUrl}
                    draftsIndexUrl={draftsIndexUrl}
                    deleteDraftUrl={deleteDraftUrl}
                    eventEndDate={eventEndDate}
                    eventStartDate={eventStartDate}
                />
            </UsersProvider>
        </App>,
        root,
    );
}
